import axios from 'axios';
import { baseUrl } from '../hooks/useEndpoints';

// ─── Types ─────────────────────────────────────────────────────────────────

export type CoachSessionStatus = 'draft' | 'published' | 'executed';
export type SessionObjective =
  | 'tactical'
  | 'physical'
  | 'technical'
  | 'recovery'
  | 'match_prep'
  | 'mixed';

export type BlockType =
  | 'sequential'
  | 'parallel'
  | 'rotation'
  | 'break'
  | 'physical';

export interface IBlockTrack {
  name: string;
  exerciseName?: string;
  assignedAthleteIds?: string[];
  workOwner?: 'coach' | 'athletic_trainer' | 'goalkeeping_coach';
  gymSchemaId?: string;
}

export type TimingMode = 'by_duration' | 'by_schedule';

export interface IBlockTiming {
  mode: TimingMode;
  durationMinutes?: number;
  scheduledStart?: string;
  scheduledEnd?: string;
}

export type ClassificationSource = 'manual' | 'auto_accepted' | 'auto_modified';

export interface IDidacticClassification {
  sessionPhaseId?: string;
  didacticMethodId?: string;
  technicalObjectiveIds?: string[];
  tacticalObjectiveIds?: string[];
  sessionPhaseSource?: ClassificationSource;
  didacticMethodSource?: ClassificationSource;
  technicalObjectivesSource?: ClassificationSource;
  tacticalObjectivesSource?: ClassificationSource;
}

export interface IBlockBase {
  _id?: string;
  order: number;
  blockType: BlockType;
  durationMinutes?: number;
  startTime?: string;
  endTime?: string;
  timing?: IBlockTiming;
  didacticClassification?: IDidacticClassification;
  ewt?: number;
  exerciseName?: string;
  staffNotes?: string;
  notes?: string;
  transitionAfterMinutes?: number;
  involvedAthleteIds?: string[];
  fieldLengthM?: number;
  fieldWidthM?: number;
  series?: number;
  reps?: string;
  load?: string;
  restSeconds?: number;
  // parallel / rotation
  tracks?: IBlockTrack[];
  // rotation only
  rotationDurationMinutes?: number;
  numberOfRotations?: number;
  // break only
  label?: string;
  // physical only
  gymSchemaId?: string;
  gymSchemaSnapshot?: {
    title?: string;
    durationMinutes?: number;
    exerciseCount?: number;
  };
  /** Bug 69f87a4b — array di schede multi-gruppo. Coesiste con gymSchemaId per back-compat. */
  physicalWorkRefs?: IPhysicalWorkRefItem[];
}

export interface IIpsBreakdown {
  s_app?: number;
  s_ewt?: number;
  s_struct?: number;
  s_tactical?: number;
}

export interface IClsBreakdown {
  decisional?: number;
  social?: number;
  tactical?: number;
}

export interface ICoachSessionMetrics {
  totalDurationMinutes?: number;
  ewtMinutes?: number;
  ewtPct?: number;
  avgIPS?: number;
  peakIPS?: number;
  avgCLS?: number;
  peakCLS?: number;
  totalCE?: number;
  estimatedAU?: number;
  loadProfile?: 'PHYSICAL_DOMINANT' | 'COGNITIVE_DOMINANT' | 'BALANCED';
  conditional?: number;
  coordinative?: number;
  cognitive?: number;
  socioAffective?: number;
  acwr?: number;
  monotonia?: number;
  strain?: number;
  ipsBreakdownAvg?: IIpsBreakdown;
  clsBreakdownAvg?: IClsBreakdown;
}

export type AthleteSessionStatus =
  | 'available'
  | 'differentiated'
  | 'injured'
  | 'absent';

export interface ICoachSessionAthlete {
  athleteId: string;
  status: AthleteSessionStatus;
  firstName?: string;
  lastName?: string;
  role?: string;
  shirtNumber?: number;
  recoveryIndex?: number;
  imageUrl?: string;
}

export interface ICoachSession {
  _id: string;
  teamId: string;
  title: string;
  scheduledDate: string;
  startTime?: string;
  status: CoachSessionStatus;
  objective?: SessionObjective;
  blocks: IBlockBase[];
  athletes?: ICoachSessionAthlete[];
  metrics?: ICoachSessionMetrics;
  keepersWorkSeparate?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ICoachSessionListItem {
  _id: string;
  teamId: string;
  title: string;
  scheduledDate: string;
  startTime?: string;
  status: CoachSessionStatus;
  objective?: SessionObjective;
  blocksCount?: number;
  athletesCount?: number;
  metrics?: ICoachSessionMetrics;
}

export interface ICreateCoachSessionBody {
  /** Team destinatario. Opzionale: in alternativa `athleteIds[]` per sessioni
   * Campo individuali o cross-team senza vincolo di team (vedi Bug 69f9ae9d). */
  teamId?: string;
  /** Atleti destinatari diretti (alternativa a teamId). Almeno uno tra i due
   * deve essere valorizzato (validato lato backend). */
  athleteIds?: string[];
  title: string;
  scheduledDate: string;
  startTime?: string;
  objective?: SessionObjective;
  status?: CoachSessionStatus;
  blocks?: Partial<IBlockBase>[];
  keepersWorkSeparate?: boolean;
  /** ID del template microciclo da cui partire. Inviato al backend ma
   * richiede supporto lato backend per essere applicato. */
  templateId?: string;
}

export interface IUpdateCoachSessionBody {
  title?: string;
  scheduledDate?: string;
  startTime?: string;
  objective?: SessionObjective;
  status?: CoachSessionStatus;
  blocks?: Partial<IBlockBase>[];
  keepersWorkSeparate?: boolean;
  athletes?: Array<{
    athleteId: string;
    status: AthleteSessionStatus;
  }>;
}

export interface ICoachSessionListQuery {
  teamId: string;
  startDate?: string;
  endDate?: string;
  status?: CoachSessionStatus;
}

// ─── Endpoints ─────────────────────────────────────────────────────────────

export interface IMoveOrDuplicateBody {
  targetDate: string;
  targetTime?: string;
}

export interface ISaveSrpeBody {
  avgAthleteRpe: number;
}

export interface ISaveDrpeBody {
  avgAthleteDRpe: {
    respiratory?: number;
    muscular?: number;
    mental?: number;
  };
}

export interface ISaveCalibrationBody {
  coachRatingEffectiveness: number;
  calibrationStatus?: 'calibrated' | 'underestimated' | 'overestimated';
}

export interface ILinkGymBody {
  gymSessionId: string;
}

export interface ICreateGymInlineBody {
  title: string;
  durationMinutes?: number;
  exercises?: Array<{
    exerciseId?: string;
    name?: string;
    series?: number;
    reps?: string;
    load?: string;
    restSeconds?: number;
  }>;
}

/**
 * Bug 69f87a4b — body per aggiungere una scheda al blocco fisico multi-card.
 * Usare `gymSessionId` (link existing) OR `title` (create inline). `assignedAthleteIds` sempre richiesto.
 */
export interface IAddPhysicalWorkRefBody {
  gymSessionId?: string;
  title?: string;
  duration?: number;
  exercises?: Array<{
    exerciseId?: string;
    name?: string;
    series?: number;
    reps?: string;
    load?: string;
    restSeconds?: number;
  }>;
  assignedAthleteIds: string[];
}

export interface IPhysicalWorkRefItem {
  refId: string;
  order: number;
  gymSessionId: string;
  createdFrom: 'linked_existing' | 'created_inline';
  createdInline?: boolean;
  workoutAssignmentId?: string;
  assignedAthleteIds: string[];
  displaySnapshot?: {
    title?: string;
    durationMinutes?: number;
    duration?: number;
    exerciseCount?: number;
  };
}

export interface IGpsSummary {
  hasGpsData: boolean;
  summary?: {
    totalDistance?: number;
    hsrDistance?: number;
    sprintCount?: number;
    accelerationCount?: number;
    gpsLoadAU?: number;
    ipsEquivalent?: number;
  };
  perAthlete?: Array<{
    athleteId: string;
    distance?: number;
    hsrDistance?: number;
    sprintCount?: number;
    accCount?: number;
    loadAU?: number;
    ipsPerceived?: number;
  }>;
  calibrationApplied?: boolean;
  calibrationDelta?: number;
}

// Backend usa `sessionType`, frontend usa `objective` — mapping bidirezionale.
// Backend usa `scheduledTime`, frontend usa `startTime` — mapping bidirezionale.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSessionIn<T extends Record<string, any>>(s: T): T {
  if (!s || typeof s !== 'object') return s;
  const withObjective =
    s.objective != null ? s : s.sessionType != null ? { ...s, objective: s.sessionType } : s;
  // Bug b3e58c: backend salva/restituisce `scheduledTime`, il frontend si aspetta `startTime`
  const withStartTime =
    withObjective.startTime != null
      ? withObjective
      : withObjective.scheduledTime != null
        ? { ...withObjective, startTime: withObjective.scheduledTime }
        : withObjective;
  return withStartTime;
}

/** Mappa il payload outgoing: `startTime` → `scheduledTime` (campo usato dal backend DTO). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSessionOut(body: Record<string, any>): Record<string, any> {
  const { objective, startTime, ...rest } = body;
  const payload: Record<string, any> = { ...rest };
  if (objective != null) payload.sessionType = objective;
  // Bug b3e58c: il DTO backend si chiama `scheduledTime`, non `startTime`
  if (startTime != null) payload.scheduledTime = startTime;
  return payload;
}

export interface ICoachExercise {
  _id: string;
  name: string;
  nameKey: string;
  lastParams?: Record<string, number | string>;
  useCount?: number;
}

export const coachSessionsEndpoints = {
  /** Libreria personale esercitazioni del coach (bug 6a204e7a, principio 3). */
  exerciseLibrary: (search?: string) =>
    axios.get<ICoachExercise[]>(`${baseUrl}/coach-sessions/exercise-library`, {
      params: search ? { search } : undefined,
    }),

  list: async (params: ICoachSessionListQuery) => {
    const res = await axios.get<ICoachSessionListItem[]>(
      `${baseUrl}/coach-sessions`,
      { params },
    );
    return { ...res, data: res.data.map(mapSessionIn) };
  },

  get: async (id: string) => {
    const res = await axios.get<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}`,
    );
    return { ...res, data: mapSessionIn(res.data) };
  },

  create: (body: ICreateCoachSessionBody) => {
    const payload = mapSessionOut(body as any);
    return axios.post<ICoachSession>(`${baseUrl}/coach-sessions`, payload);
  },

  update: (id: string, body: IUpdateCoachSessionBody, ifMatch?: number) => {
    const payload = mapSessionOut(body as any);
    return axios.patch<ICoachSession>(`${baseUrl}/coach-sessions/${id}`, payload, {
      headers:
        ifMatch !== undefined ? { 'If-Match': String(ifMatch) } : undefined,
    });
  },

  delete: (id: string) =>
    axios.delete<{ ok: true }>(`${baseUrl}/coach-sessions/${id}`),

  recalculateMetrics: (id: string) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/recalculate-metrics`,
      {},
    ),

  /**
   * Aggiorna lo status (available/differentiated/injured/absent) di un singolo
   * atleta nella sessione. Persiste in rosterSnapshot e ritorna la sessione
   * aggiornata con `athletes[]` ricalcolato.
   */
  setAthleteStatus: (
    id: string,
    body: { athleteId: string; status: AthleteSessionStatus },
  ) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/athlete-status`,
      body,
    ),

  duplicate: (id: string, body: IMoveOrDuplicateBody) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/duplicate`,
      body,
    ),

  move: (id: string, body: IMoveOrDuplicateBody) =>
    axios.post<ICoachSession>(`${baseUrl}/coach-sessions/${id}/move`, body),

  saveSrpe: (id: string, body: ISaveSrpeBody) =>
    axios.post<ICoachSession>(`${baseUrl}/coach-sessions/${id}/srpe`, body),

  saveDrpe: (id: string, body: ISaveDrpeBody) =>
    axios.post<ICoachSession>(`${baseUrl}/coach-sessions/${id}/drpe`, body),

  saveCalibration: (id: string, body: ISaveCalibrationBody) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/calibration`,
      body,
    ),

  linkGym: (id: string, blockOrder: number, body: ILinkGymBody) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/link-gym`,
      body,
    ),

  createGymInline: (
    id: string,
    blockOrder: number,
    body: ICreateGymInlineBody,
  ) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/create-gym-inline`,
      body,
    ),

  unlinkGym: (id: string, blockOrder: number) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/unlink-gym`,
      {},
    ),

  syncGym: (id: string, blockOrder: number) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/sync-gym`,
      {},
    ),

  // Bug 69f87a4b — Multi-scheda multi-gruppo
  addPhysicalWorkRef: (
    id: string,
    blockOrder: number,
    body: IAddPhysicalWorkRefBody,
  ) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/physical-refs`,
      body,
    ),

  updatePhysicalWorkRef: (
    id: string,
    blockOrder: number,
    refId: string,
    body: { assignedAthleteIds: string[] },
  ) =>
    axios.patch<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/physical-refs/${refId}`,
      body,
    ),

  deletePhysicalWorkRef: (id: string, blockOrder: number, refId: string) =>
    axios.delete<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/physical-refs/${refId}`,
    ),

  duplicatePhysicalWorkRef: (id: string, blockOrder: number, refId: string) =>
    axios.post<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/physical-refs/${refId}/duplicate`,
      {},
    ),

  reorderPhysicalWorkRefs: (
    id: string,
    blockOrder: number,
    body: { orderedRefIds: string[] },
  ) =>
    axios.patch<ICoachSession>(
      `${baseUrl}/coach-sessions/${id}/blocks/${blockOrder}/physical-refs:reorder`,
      body,
    ),

  matchGps: (id: string) =>
    axios.post<{ queued: boolean; jobId?: string }>(
      `${baseUrl}/coach-sessions/${id}/match-gps`,
      {},
    ),

  getGpsSummary: (id: string) =>
    axios.get<IGpsSummary>(`${baseUrl}/coach-sessions/${id}/gps-summary`),

  calibrateIps: (id: string) =>
    axios.post<{ applied: boolean; delta?: number }>(
      `${baseUrl}/coach-sessions/${id}/calibrate-ips`,
      {},
    ),

  analyzeTagWeightBias: (teamId: string, lookbackDays = 60) =>
    axios.get<
      Array<{
        tag: string;
        sampleSize: number;
        deltaAvg: number;
        suggestedAdjustment: number;
        actionable: boolean;
      }>
    >(`${baseUrl}/coach-sessions/tag-weight-bias/${teamId}`, {
      params: { lookbackDays },
    }),
};
